import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/apiClient';
import { NZ_AIRPORTS_DATA } from '../data/locations';

interface LocationInfo {
  id: number;
  icao_code: string;
  name: string;
  country_code: string | null;
}

interface UseAvailableLocationsResult {
  availableLocations: LocationInfo[];
  loading: boolean;
  error: string | null;
  hasFullNZAccess: boolean;
}

/**
 * Fetches the list of locations available to the current user from the backend.
 *
 * The backend filters locations based on user authorization (individual assignments,
 * organization membership, and roles), so this returns only what the user can access.
 *
 * Uses React Query for automatic caching, deduplication, and background refresh.
 * - Cached for 5 minutes (staleTime)
 * - Auto-refreshes every 5 minutes in background
 * - Deduplicates concurrent requests automatically
 *
 * @returns Object containing available locations, loading state, error, and NZ access check
 */
export function useAvailableLocations(): UseAvailableLocationsResult {
  const { data, isLoading, error } = useQuery({
    queryKey: ['locations'],
    queryFn: () => apiClient.get<LocationInfo[]>('/api/locations'),
  });

  const availableLocations = data || [];

  const nzLocationIds = new Set(availableLocations.filter(loc => loc.country_code === 'NZ').map(loc => loc.id));
  const hasFullNZAccess = NZ_AIRPORTS_DATA.every(airport => nzLocationIds.has(airport.id));

  return {
    availableLocations,
    loading: isLoading,
    error: error?.message || null,
    hasFullNZAccess
  };
}
